import { IconButton, MenuItem, Stack } from "@mui/material";
import { Control, FieldErrors, UseFormWatch } from "react-hook-form";
import SearchIcon from "@mui/icons-material/Search";
import { TSale } from "../../../../core/types/Sale";
import TextFieldRequired from "../../../Inputs/TextFieldRequired";
import { numberOnly } from "../../../../masks/masks";
import SelectRequired from "../../../Inputs/SelectRequired";
import states from "../../../../core/data/states";
import React from "react";
import { useGatewayContext } from "../../../../core/contexts/GatewayContext";
import InternationalAddress from "../../../Checkout/InternationalAddress";

export interface ICheckoutAddress3 {
    control: Control<TSale, any>;
    errors: FieldErrors<TSale>;
    loading: boolean;
    searchedZipCode: boolean;
    watch: UseFormWatch<TSale>;
    saveLostSale: () => void;
    clearErrors: () => void;
    searchZipCode: (loading: boolean, zipCode: string) => void;
    handleAddresNumberKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    handleAddresComplementKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    onSendPixel: () => void;
}

const CheckoutAddress3 = ({
    control,
    errors,
    loading,
    searchedZipCode,
    watch,
    saveLostSale,
    clearErrors,
    searchZipCode,
    handleAddresNumberKeyDown,
    handleAddresComplementKeyDown,
    onSendPixel,

}: ICheckoutAddress3) => {
    const { useStripe: useStripePayment } = useGatewayContext();

    if (useStripePayment) {
        return <InternationalAddress control={control} errors={errors} />;
    }

    return (
        <Stack
            direction="column"
            spacing={2}
            data-cy="containerAddress"
        >
            <TextFieldRequired
                control={control}
                fieldName="address.zipCode"
                label="CEP"
                placeholder="00000-000"
                inputProps={{
                    inputMode: "numeric",
                    maxLength: 9,
                    "data-cy": "addressZipCode",
                    "data-testid": "addressZipCode",
                }}
                dataCyErrorLabel="addressZipCodeError"
                onFieldExit={saveLostSale}
                clearErrors={clearErrors}
                validate={(value) => (!value) || (value == '') ? true : numberOnly(value).length === 8}
                endAdornment={
                    <IconButton
                        size="small"
                        onClick={() => searchZipCode(loading, watch("address.zipCode"))}
                    >
                        <SearchIcon />
                    </IconButton>
                }
            />

            <Stack
                direction="column"
                spacing={2}
                sx={{ display: searchedZipCode ? "block" : "none" }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextFieldRequired
                        control={control}
                        fieldName="address.street"
                        label="Endereço"
                        clearErrors={clearErrors}
                        inputProps={{
                            "data-cy": "addressStreet",
                            "data-testid": "addressStreet",
                        }}
                        dataCyErrorLabel="addressStreetError"
                    />
                    <TextFieldRequired
                        control={control}
                        fieldName="address.number"
                        label="Número"
                        onKeyDown={handleAddresNumberKeyDown}
                        clearErrors={clearErrors}
                        inputProps={{
                            "data-cy": "addressNumber",
                            "data-testid": "addressNumber",
                        }}
                        dataCyErrorLabel="addressNumberError"
                    />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextFieldRequired
                        control={control}
                        fieldName="address.neighborhood"
                        label="Bairro"
                        clearErrors={clearErrors}
                        inputProps={{
                            "data-cy": "addressNeighborhood",
                            "data-testid": "addressNeighborhood",
                        }}
                        dataCyErrorLabel="addressNeighborhoodError"
                    />
                    <TextFieldRequired
                        control={control}
                        fieldName="address.complement"
                        label="Complemento"
                        required={false}
                        onKeyDown={handleAddresComplementKeyDown}
                        clearErrors={clearErrors}
                    />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextFieldRequired
                        control={control}
                        fieldName="address.city"
                        label="Cidade"
                        clearErrors={clearErrors}
                        onFieldExit={onSendPixel}
                        inputProps={{
                            "data-cy": "addressCity",
                            "data-testid": "addressCity",
                        }}
                        dataCyErrorLabel="addressCityError"
                    />
                    <SelectRequired
                        control={control}
                        fieldName="address.state"
                        label="Estado"
                        onFieldExit={onSendPixel}
                        inputProps={{
                            "data-cy": "addressState",
                            "data-testid": "addressState",
                        }}
                    >
                        {states.map((state) => (
                            <MenuItem key={`state${state.sigla}`} value={state.sigla}>{state.nome}</MenuItem>
                        ))}
                    </SelectRequired>
                </Stack>
            </Stack>
        </Stack>
    );
}

export default CheckoutAddress3;